import React from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiChevronLeft } from 'react-icons/fi';
import { AuthWordmark } from '../../Login/AuthLayout';
import LanguageSwitch from '../../../components/LanguageSwitch';
import { useMessages } from '../../../i18n/I18n';
import './AdminLayout.css';

interface AdminLayoutProps {
  title: string;
  description: string;
  /** Top right of the header */
  headerAction?: React.ReactNode;
  /** The tools link back to the admin home, which has no such link */
  hasBackLink?: boolean;
  /** Replaces the back link's in-app navigation */
  onBack?: () => void;
  className?: string;
  children: React.ReactNode;
}

/** The page every /admin tool shares: the brand on top, then a centered column with the tool's title and content */
const AdminLayout: React.FC<AdminLayoutProps> = ({
  title,
  description,
  headerAction,
  hasBackLink = false,
  onBack,
  className,
  children,
}) => {
  const t = useMessages('admin');
  return (
    <div className={className ? `admin-page ${className}` : 'admin-page'}>
      <header className="admin-header">
        <div className="admin-brand">
          <AuthWordmark />
          <span className="admin-brand-divider" aria-hidden="true" />
          <span className="admin-brand-section">Admin</span>
        </div>
        <div className="admin-header-actions">
          <LanguageSwitch />
          {headerAction}
        </div>
      </header>
      <main className="admin-main">
        {hasBackLink && (onBack ? (
          <button type="button" className="admin-back" onClick={onBack}>
            <FiChevronLeft aria-hidden="true" />
            {t.adminTools}
          </button>
        ) : (
          <Link to="/admin" className="admin-back">
            <FiChevronLeft aria-hidden="true" />
            {t.adminTools}
          </Link>
        ))}
        <div className="admin-title">
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {children}
      </main>
    </div>
  );
};

const BANNER_ICONS = {
  danger: FiAlertCircle,
  warning: FiAlertTriangle,
  success: FiCheckCircle,
};

interface AdminBannerProps {
  tone: keyof typeof BANNER_ICONS;
  children: React.ReactNode;
}

/** An error, warning or success message, in its status color */
export const AdminBanner: React.FC<AdminBannerProps> = ({ tone, children }) => {
  const Icon = BANNER_ICONS[tone];
  return (
    <div className={`admin-banner ${tone}`} role={tone === 'success' ? 'status' : 'alert'}>
      <Icon aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
};

export default AdminLayout;
